import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { AiSearchToolCallCreateStore } from "../../store";
import { InputNumber } from "../../../common/controls";
import { InputString } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { InputText } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type AiSearchToolCallCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  sequenceSelectedValue?: { displayId: string; id: string } | null;
  onSequenceChange?: (value: number) => void;
  sequence: number;
  sequenceError?: string;
  toolNameSelectedValue?: { displayId: string; id: string } | null;
  onToolNameChange?: (value: string) => void;
  toolName: string;
  toolNameError?: string;
  patternSelectedValue?: { displayId: string; id: string } | null;
  onPatternChange?: (value: string) => void;
  pattern: string;
  patternError?: string;
  navigationPathSelectedValue?: { displayId: string; id: string } | null;
  onNavigationPathChange?: (value: string) => void;
  navigationPath: string;
  navigationPathError?: string;
  toolArgumentsSelectedValue?: { displayId: string; id: string } | null;
  onToolArgumentsChange?: (value: string) => void;
  toolArguments: string;
  toolArgumentsError?: string;
  resultSelectedValue?: { displayId: string; id: string } | null;
  onResultChange?: (value: string) => void;
  result: string;
  resultError?: string;
  durationMsSelectedValue?: { displayId: string; id: string } | null;
  onDurationMsChange?: (value: number) => void;
  durationMs: number;
  durationMsError?: string;
  successSelectedValue?: { displayId: string; id: string } | null;
  onSuccessChange?: (value: boolean) => void;
  success: boolean;
  successError?: string;
  errorMessageSelectedValue?: { displayId: string; id: string } | null;
  onErrorMessageChange?: (value: string) => void;
  errorMessage: string;
  errorMessageError?: string;
  aiSearchToolCallCreateStore: AiSearchToolCallCreateStore;
};

export const AiSearchToolCallCreateForm: React.FC<AiSearchToolCallCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      sequence = 0,
      sequenceError,
      onSequenceChange = () => {},
      toolName = "",
      toolNameError,
      onToolNameChange = () => {},
      pattern = "",
      patternError,
      onPatternChange = () => {},
      navigationPath = "",
      navigationPathError,
      onNavigationPathChange = () => {},
      toolArguments = "{}",
      toolArgumentsError,
      onToolArgumentsChange = () => {},
      result = "{}",
      resultError,
      onResultChange = () => {},
      durationMs = 0,
      durationMsError,
      onDurationMsChange = () => {},
      success = false,
      successError,
      onSuccessChange = () => {},
      errorMessage = "",
      errorMessageError,
      onErrorMessageChange = () => {},
      aiSearchToolCallCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="AiSearchToolCall"
              mode="create"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "sequence":
                    onSequenceChange?.(valor);
                    break;
                  case "toolName":
                    onToolNameChange?.(valor);
                    break;
                  case "pattern":
                    onPatternChange?.(valor);
                    break;
                  case "navigationPath":
                    onNavigationPathChange?.(valor);
                    break;
                  case "toolArguments":
                    onToolArgumentsChange?.(valor);
                    break;
                  case "result":
                    onResultChange?.(valor);
                    break;
                  case "durationMs":
                    onDurationMsChange?.(valor);
                    break;
                  case "success":
                    onSuccessChange?.(valor);
                    break;
                  case "errorMessage":
                    onErrorMessageChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={aiSearchToolCallCreateStore.getCardErrors("null")}
              title={langStore.groupAiSearchToolCallLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputNumber
                  autoFocus
                  value={sequence}
                  error={Boolean(sequenceError)}
                  warning={
                    !sequenceError &&
                    Boolean(aiSearchToolCallCreateStore.warnings["sequence"])
                  }
                  helperText={
                    sequenceError ||
                    aiSearchToolCallCreateStore.warnings["sequence"]?.message
                  }
                  allowNegative={false}
                  label={langStore.AiSearchToolCallSequenceLabel}
                  decimalScale={0}
                  description={langStore.AiSearchToolCallSequenceDescription}
                  onChange={(value: number) => onSequenceChange(value)}
                />

                <InputString
                  value={toolName}
                  error={Boolean(toolNameError)}
                  warning={
                    !toolNameError &&
                    Boolean(aiSearchToolCallCreateStore.warnings["toolName"])
                  }
                  helperText={
                    toolNameError ||
                    aiSearchToolCallCreateStore.warnings["toolName"]?.message
                  }
                  label={langStore.AiSearchToolCallToolNameLabel}
                  onChange={(value: string) => onToolNameChange(value)}
                  description={langStore.AiSearchToolCallToolNameDescription}
                />

                <InputString
                  value={pattern}
                  error={Boolean(patternError)}
                  warning={
                    !patternError &&
                    Boolean(aiSearchToolCallCreateStore.warnings["pattern"])
                  }
                  helperText={
                    patternError ||
                    aiSearchToolCallCreateStore.warnings["pattern"]?.message
                  }
                  label={langStore.AiSearchToolCallPatternLabel}
                  onChange={(value: string) => onPatternChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.AiSearchToolCallPatternDescription}
                />

                <InputString
                  value={navigationPath}
                  error={Boolean(navigationPathError)}
                  warning={
                    !navigationPathError &&
                    Boolean(
                      aiSearchToolCallCreateStore.warnings["navigationPath"]
                    )
                  }
                  helperText={
                    navigationPathError ||
                    aiSearchToolCallCreateStore.warnings["navigationPath"]
                      ?.message
                  }
                  label={langStore.AiSearchToolCallNavigationPathLabel}
                  onChange={(value: string) => onNavigationPathChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.AiSearchToolCallNavigationPathDescription
                  }
                />

                <InputCodeEditor
                  value={toolArguments}
                  error={Boolean(toolArgumentsError)}
                  helperText={
                    toolArgumentsError ||
                    aiSearchToolCallCreateStore.warnings["toolArguments"]
                      ?.message
                  }
                  label={langStore.AiSearchToolCallToolArgumentsLabel}
                  onChange={(value: string) => onToolArgumentsChange(value)}
                  language="json"
                  description={
                    langStore.AiSearchToolCallToolArgumentsDescription
                  }
                />

                <InputCodeEditor
                  value={result}
                  error={Boolean(resultError)}
                  helperText={
                    resultError ||
                    aiSearchToolCallCreateStore.warnings["result"]?.message
                  }
                  label={langStore.AiSearchToolCallResultLabel}
                  onChange={(value: string) => onResultChange(value)}
                  language="json"
                  description={langStore.AiSearchToolCallResultDescription}
                />
                <InputNumber
                  value={durationMs}
                  error={Boolean(durationMsError)}
                  warning={
                    !durationMsError &&
                    Boolean(aiSearchToolCallCreateStore.warnings["durationMs"])
                  }
                  helperText={
                    durationMsError ||
                    aiSearchToolCallCreateStore.warnings["durationMs"]?.message
                  }
                  allowNegative={false}
                  label={langStore.AiSearchToolCallDurationMsLabel}
                  decimalScale={0}
                  description={langStore.AiSearchToolCallDurationMsDescription}
                  onChange={(value: number) => onDurationMsChange(value)}
                />
                <InputBoolean
                  value={success}
                  error={Boolean(successError)}
                  helperText={
                    successError ||
                    aiSearchToolCallCreateStore.warnings["success"]?.message
                  }
                  label={langStore.AiSearchToolCallSuccessLabel}
                  onChange={(value: boolean) => onSuccessChange(value)}
                />

                <InputText
                  value={errorMessage}
                  error={Boolean(errorMessageError)}
                  warning={
                    !errorMessageError &&
                    Boolean(
                      aiSearchToolCallCreateStore.warnings["errorMessage"]
                    )
                  }
                  helperText={
                    errorMessageError ||
                    aiSearchToolCallCreateStore.warnings["errorMessage"]
                      ?.message
                  }
                  label={langStore.AiSearchToolCallErrorMessageLabel}
                  onChange={(value: string) => onErrorMessageChange(value)}
                  nullText={langStore.optionalFieldHint}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
